import moment from 'moment';
import { TaskSummary } from '../../tasksSummary/TasksSummaryRepository';
import { separateName, removeTagsAndStyles } from '../../helpers/commonHelper';
import { IndividualNameSummary } from '../selectors/IndividualSelector';
import { OrganisationIAutoCompleteItem } from '../selectors/OrganisationSelector';
import { EventAndCampaignAttendeeSummary } from '../../eventsAndCampaignsAttendee/EventsAndCampaignsAttendeeRepositories';
import { EventsAndCampaignIAutoCompleteItem } from '../../notesSummary/selectors/EventsAndCampaignSelector';
import { NameSummary, PartyAddressesData } from '../../nameSummary/NameSummaryRepository';
import { Partydetails, PhoneNumber, EmailAddress } from '../../ClientAndContactDetails/ClientAndContactDetailsRepositories';
import type { Enquiry, EnquiryFormProps } from './EnquiryFormDrawer';

// Explicit request (EnquiryFormDrawer split): the initial form values used to be built inline in the
// component body on every render. They are a pure function of the props, the party-addresses query and
// the reminder resolver, so they live here unchanged. This also keeps the component's own control flow
// small enough for eslint-plugin-react-hooks (v1) to analyse - its path counting loses precision on
// very branchy component bodies and then reports every later hook as "called conditionally".
export const getFirstAddressOfEachType = (data: PartyAddressesData) => {
    const addressTypes = new Set();
    const firstAddresses = [];

    for (const address of data.party.addresses.address) {
        if (!addressTypes.has(address.addressType)) {
            addressTypes.add(address.addressType);
            firstAddresses.push(address);
        }
    }

    return firstAddresses;
};

export const buildEnquiryInitialValues = (
    props: EnquiryFormProps,
    partyAddressesQuery: { data?: PartyAddressesData; loading: boolean },
    getReminderValue: (isAssignment: boolean) => { label: string; value: string },
): Enquiry => {
    let initialValues: Enquiry = {
        isBookMeetingEmailDialogOpen: false,
        isClosed: false,
        firstName: '',
        lastName: '',
        individualSelector: null,
        isNameSwitched: false, // props.taskSummary && props.taskSummary.fromName && props.taskSummary.fromNameGuid ? true : false,
        nameGuid: null,
        email: '',
        gender: 'Unknown',
        initialEnquiryDate: moment().toDate(),
        phone: '',
        mobile: '',
        source: null,
        office: null,
        sourceNotes: '',

        organisationSelector: null,
        isOrganisationSwitched: false, // props.taskSummary && props.taskSummary.organisation && props.taskSummary.organisationGuid ? true : false,
        organisation: '',
        organisationGuid: null,
        positionAtOrganisation: '',

        practiceAreaGuidID: null,
        areaOfLaw: '',
        natureOfEnquiry: '',

        internalNotes: '',
        files: [],

        assignedTo: null,
        assignedToName: null, // Display Name
        assignedBy: props.assignedBy,
        assignedByName: props.assignedByName, // Display Name
        dateAssigned: moment().toDate(),
        followUpDueDate: null,
        assignmentFollowUpDueReminder: getReminderValue(true),

        // followUpAction: 'Email',
        followUpAction: null,
        followUpActionSystemName: null,
        followUpSystemAction: null,
        followUpDate: null,
        // outcome: 'More Follow Up',
        outcome: null,
        outcomeDate: null,
        outcomeNotes: '',
        reason: null,
        reasonGuidID: null,
        followUpNotes: null,
        hasFollowUpNotesMentions: false,
        followUpDueReminder: null,
        autoFollowUpEmailTemplate: props.enquiryDefaultConfig && props.enquiryDefaultConfig.emailTemplateGuid && props.enquiryDefaultConfig.emailTemplateName ?
            {
                label: props.enquiryDefaultConfig.emailTemplateName,
                value: props.enquiryDefaultConfig.emailTemplateGuid
            } : null,
        isAutoFollowUp: props.enquiryDefaultConfig?.isAutoFollowUp ? props.enquiryDefaultConfig.isAutoFollowUp : false,

        handleCreateAndClose: false,
        handleCreateAndNew: false,
        handleUpdate: false,
        // showDiscard: false,
        // isFormChanged: false,
        isAssignmentExpand: true,
        isKeyOpportunity: props.isKeyOpportunity,
        isNotProceeding: false,
        notProceedingReason: null,
        notProceedingReasonGuid: null,

        referrerGuid: null,
        referrerName: null,
        isReferrerFromDataSource: null,
        isShowReferrer: false,

        staff: null,
        startTime: null, // moment().add(1, 'hour').toDate(),
        endTime: null, // moment().add(2, 'hour').toDate(),
        isAllDay: false,
        officeLocation: null,
        feeApplicable: null,
        isSendConfirmation: true,
        meetingNotes: null,
        timezone: null,

        periodMeetingDefaultReminder: props.enquiryDefaultConfig?.periodMeetingDefaultReminder ? { label: props.enquiryDefaultConfig?.periodMeetingDefaultReminder, value: props.enquiryDefaultConfig?.periodMeetingDefaultReminder } : null,
        isSendMeetingDefaultReminderSms: props.enquiryDefaultConfig?.isSendMeetingDefaultReminderSms ? props.enquiryDefaultConfig?.isSendMeetingDefaultReminderSms : false,
        isSendMeetingDefaultReminderEmail: props.enquiryDefaultConfig?.isSendMeetingDefaultReminderEmail ? props.enquiryDefaultConfig?.isSendMeetingDefaultReminderEmail : false,

        emailTemplate: null,

        isContactDetailsChanged: false,
        isUpdateContacts: false,
        isSentAcknowledgementEmail: false,
        updateFollowUpDueForInternalNote: true,
        outcomeDateChanged: false,

        estimatedFees: null,
        isEstimatedFeesIncludesTax: false,
        estimatedDisbursements: null,
        isEstimatedDisbursementsIncludesTax: false,
        enquiryQuality: 3,
        likelihoodToProceed: 3,
        clientFirstName: null,
        clientLastName: null,
        dOB: null,
        isMarketingConsent: true,
        isConflictCheckPerformed: false,
        isTermsAndConditionsApproved: false,
        isIdentityCheckPerformed: false,
        isPrivacyStatementProvided: false,
        addressName: null,
        floor: null,
        number: null,
        street: null,
        street2: null,
        suburb: null,
        state: null,
        postcode: null,
        country: null,
        pOBox: null,
        postalSuburb: null,
        postalState: null,
        postalPostCode: null,
        postalCountry: null,
        isNoConflict: false,
        isShowNotProceedingReferrer: false,
        isNotProceedingReferrerSwitched: false,
        referredToFirmSelector: null,
        referredToFirmGuid: null,
        referredToFirmName: null,
        notProceedingOutcomeNotes: null,
        // Fields inside proceeding section
        isShowNotProceedingReferrerInProceeding: false,
        isNotProceedingReferrerSwitchedInProceeding: false,
        referredToFirmSelectorInProceeding: null,
        referredToFirmGuidInProceeding: null,
        referredToFirmNameInProceeding: null,
        notProceedingOutcomeNotesInProceeding: null,
        clientRelationshipToEnquirer: null,
        matterId: null,
        matterGuid: null,
        matterTitle: null,
        matterFileNumber: null,
        matterSelector: null,
        pOBoxType: props.pOBoxType,
        enquiryNotes: null,

        otherPartyFirstName: null,
        otherPartyLastName: null,
        otherPartyNotes: null,
        otherPartyEmail: null,
        otherPartyMobile: null,
        otherPartyPhone: null,
        otherPartyOrganisation: null,
        otherPartyDOB: null,
        otherPartyAddressName: null,
        otherPartyFloor: null,
        otherPartyNumber: null,
        otherPartyStreet: null,
        otherPartyStreet2: null,
        otherPartySuburb: null,
        otherPartyState: null,
        otherPartyPostcode: null,
        otherPartyCountry: null,

        otherPartyPOBoxType: props.pOBoxType,
        otherPartyPOBox: null,
        otherPartyPostalSuburb: null,
        otherPartyPostalState: null,
        otherPartyPostalPostCode: null,
        otherPartyPostalCountry: null,
        isOtherPartyNameSwitched: false,
        otherPartyNameGuid: null,
        otherPartyOrganisationGuid: null,
        otherPartyIndividualSelector: null,

        isSavedDirectly: false,

        isRiskAssessed: null,
        riskLevelSelector: null,
        riskNotes: null,

        secondClientFirstName: null,
        secondClientLastName: null,
        secondClientRelationshipToEnquirer: null,
        secondClientGender: 'Unknown',
        secondClientSuburb: null,
        secondClientState: null,
        secondClientPostCode: null,
        secondClientIsMarketingConsent: true,
        secondClientIsTermsAndConditionsApproved: false,
        secondClientIsPrivacyStatementProvided: false,
        secondClientIsConflictCheckPerformed: false,
        secondClientIsIdentityCheckPerformed: false,
        secondClientIsNoConflict: false,
        secondClientCountry: null,
        secondClientAddressName: null,
        secondClientFloor: null,
        secondClientNumber: null,
        secondClientStreet1: null,
        secondClientStreet2: null,
        secondClientDOB: null,
        secondClientPOBoxType: props.pOBoxType,
        secondClientPOBox: null,
        secondClientPostalSuburb: null,
        secondClientPostalState: null,
        secondClientPostalPostCode: null,
        secondClientPostalCountry: null,

        responseText: null,

        // bot integration
        followUpBotUrl: null,

        // events and campaigns
        eventAndCampaignSelector: null,
        eventsAndCampaignNotes: null,

        title: 'None',
        clientTitle: 'None',
        secondClientTitle: 'None',
        clientEmail: null,
        clientGuid: null,
        clientMobile: null,
        clientPhone: null,
        otherPartyTitle: 'None',
        secondClientGuid: null,
        secondClientEmail: null,
        secondClientMobile: null,
        secondClientPhone: null,
        clientIndividualSelector: null,
        isClientNameSwitched: false,
        secondClientIndividualSelector: null,
        isSecondClientNameSwitched: false,

        clickThroughUrl: null,
        utmSource: null,
        utmMedium: null,
        utmCampaign: null,
        utmContent: null,
        utmKeyword: null,
        googleClickIdentifier: null,
        facebookClickIdentifier: null,
    };

    const getTaskSummaryIndividualSelectorValue = (taskSummary?: TaskSummary) => {
        if (!taskSummary) {
            return null;
        }

        if (!(props.taskSummary && props.taskSummary.fromName && props.taskSummary.fromNameGuid)) {
            return null;
        }

        return {
            label: props.taskSummary.fromName,
            value: props.taskSummary.fromNameGuid,
            nameId: props.taskSummary.fromNameId,
            guidID: props.taskSummary.fromNameGuid,
            // isFromDataSource: taskSummary.isFromDataSource 
        };
    };

    // get all possible data from task summary to enquiry form
    if (props.taskSummary) {
        initialValues = {
            ...initialValues,
            firstName: props.taskSummary && props.taskSummary.fromName ? separateName(props.taskSummary.fromName).firstName : '',
            lastName: props.taskSummary && props.taskSummary.fromName ? separateName(props.taskSummary.fromName).lastName : '',
            individualSelector: getTaskSummaryIndividualSelectorValue(props.taskSummary) as IndividualNameSummary,
            nameGuid: props.taskSummary && props.taskSummary.fromNameGuid ? props.taskSummary.fromNameGuid : null,
            phone: props.taskSummary ? props.taskSummary.phoneNumber ?? '' : '',
            organisationSelector:
                props.taskSummary && props.taskSummary.organisation && props.taskSummary.organisationGuid
                    ? { label: props.taskSummary.organisation, value: props.taskSummary.organisationGuid } as OrganisationIAutoCompleteItem
                    : null,
            organisation: props.taskSummary ? props.taskSummary.organisation ?? '' : '',
            organisationGuid: props.taskSummary && props.taskSummary.organisationGuid ? props.taskSummary.organisationGuid : null,
            natureOfEnquiry: props.taskSummary && props.taskSummary.notes ? removeTagsAndStyles(props.taskSummary.notes) : '',
        };
    }

    const getEventsIndividualSelectorValue = (attendee?: EventAndCampaignAttendeeSummary) => {

        if (!attendee) {
            return null;
        }

        if (!attendee.name && !attendee.entityGuid && !attendee.nameId) {
            return null;
        }

        return {
            label: attendee.name,
            value: attendee.entityGuid,
            nameId: attendee.nameId,
            guidID: attendee.entityGuid,
            isFromDataSource: attendee.entityTypeId === 0 ? true : false
        };
    };

    // get all possible data from event and campaign attendee summary to enquiry form
    if (props.eventAndCampaignAttendeeSummary) {
        initialValues = {
            ...initialValues,

            // individual
            firstName:
                props.eventAndCampaignAttendeeSummary && props.eventAndCampaignAttendeeSummary.name ? separateName(props.eventAndCampaignAttendeeSummary.name).firstName : '',
            lastName: props.eventAndCampaignAttendeeSummary && props.eventAndCampaignAttendeeSummary.name ? separateName(props.eventAndCampaignAttendeeSummary.name).lastName : '',

            ...(props.eventAndCampaignAttendeeSummary &&
                (props.eventAndCampaignAttendeeSummary.entityTypeId === 0 || props.eventAndCampaignAttendeeSummary.entityTypeId === 2) && {
                individualSelector: getEventsIndividualSelectorValue(props.eventAndCampaignAttendeeSummary) as IndividualNameSummary,
                nameGuid: props.eventAndCampaignAttendeeSummary && props.eventAndCampaignAttendeeSummary.entityGuid ? props.eventAndCampaignAttendeeSummary.entityGuid : null,
            }),

            phone: props.eventAndCampaignAttendeeSummary ? props.eventAndCampaignAttendeeSummary.phone ?? '' : '',
            mobile: props.eventAndCampaignAttendeeSummary ? props.eventAndCampaignAttendeeSummary.mobile ?? '' : '',
            email: props.eventAndCampaignAttendeeSummary ? props.eventAndCampaignAttendeeSummary.email ?? '' : '',

            // organistaion
            organisation: props.eventAndCampaignAttendeeSummary ? props.eventAndCampaignAttendeeSummary.organisation ?? '' : '',
            organisationSelector:
                props.eventAndCampaignAttendeeSummary && props.eventAndCampaignAttendeeSummary.organisation && props.eventAndCampaignAttendeeSummary.organisationGuid
                    ? ({
                        label: props.eventAndCampaignAttendeeSummary.organisation,
                        value: props.eventAndCampaignAttendeeSummary.organisationGuid,
                    } as OrganisationIAutoCompleteItem)
                    : null,
            organisationGuid:
                props.eventAndCampaignAttendeeSummary && props.eventAndCampaignAttendeeSummary.organisationGuid ? props.eventAndCampaignAttendeeSummary.organisationGuid : null,
            natureOfEnquiry:
                props.eventAndCampaignAttendeeSummary && props.eventAndCampaignAttendeeSummary.attendanceNotes
                    ? removeTagsAndStyles(props.eventAndCampaignAttendeeSummary.attendanceNotes)
                    : '',

            eventAndCampaignSelector:
                props.eventAndCampaignAttendeeSummary && props.eventAndCampaignAttendeeSummary.eventAndCampaignName && props.eventAndCampaignAttendeeSummary.eventAndCampaignGuid
                    ? ({
                        label: props.eventAndCampaignAttendeeSummary.eventAndCampaignName,
                        value: props.eventAndCampaignAttendeeSummary.eventAndCampaignGuid,
                    } as EventsAndCampaignIAutoCompleteItem)
                    : null,
            eventsAndCampaignNotes: props.eventAndCampaignAttendeeSummary ? props.eventAndCampaignAttendeeSummary.attendanceNotes : null,
        };
    }

    const getNameSummaryIndividualSelectorValue = (nameSummary?: NameSummary) => {

        if (!nameSummary) {
            return null;
        }

        if (!nameSummary.name && !nameSummary.guidID && !nameSummary.nameId) {
            return null;
        }

        return {
            label: nameSummary.name,
            value: nameSummary.guidID,
            nameId: nameSummary.nameId,
            guidID: nameSummary.guidID,
            isFromDataSource: nameSummary.isFromDataSource
        };
    };

    if (props.nameSummary) {

        initialValues = {
            ...initialValues,

            // individual
            firstName: props.nameSummary && !props.nameSummary.isOrganisation && props.nameSummary.name ? separateName(props.nameSummary.name).firstName : '',
            lastName: props.nameSummary && !props.nameSummary.isOrganisation && props.nameSummary.name ? separateName(props.nameSummary.name).lastName : '',

            individualSelector: props.nameSummary && !props.nameSummary.isOrganisation ? getNameSummaryIndividualSelectorValue(props.nameSummary) as IndividualNameSummary : null,
            nameGuid: props.nameSummary && !props.nameSummary.isOrganisation ? props.nameSummary.guidID : null,
            // ...(props.nameSummary && props.nameSummary.entityTypeId === 0 && {   
            //     individualSelector: getEventsIndividualSelectorValue(props.nameSummary) as IndividualNameSummary,
            //     nameGuid: props.nameSummary && props.nameSummary.entityGuid ? props.nameSummary.entityGuid : null,
            // }),

            phone: props.nameSummary ? props.nameSummary.phone ?? '' : '',
            mobile: props.nameSummary ? props.nameSummary.mobile ?? '' : '',
            email: props.nameSummary ? props.nameSummary.emailAddress ?? '' : '',

            // organistaion
            organisation: props.nameSummary && props.nameSummary.isOrganisation ? props.nameSummary.name ?? '' : '',
            organisationGuid: props.nameSummary && props.nameSummary.isOrganisation ? props.nameSummary.guidID : null,
            organisationSelector: props.nameSummary && props.nameSummary.name && props.nameSummary.guidID
                ? {
                    ...props.nameSummary,
                    label: props.nameSummary.name,
                    value: props.nameSummary.guidID
                } as unknown as OrganisationIAutoCompleteItem
                : null,
            isOrganisationSwitched: false,
            // organisationGuid: props.nameSummary && props.nameSummary.organisationGuid ? props.nameSummary.organisationGuid : null,
            // natureOfEnquiry: props.nameSummary && props.nameSummary.notes ? removeTagsAndStyles(props.nameSummary.notes)  : '',
        };
    }

    const getPartyDetailsIndividualSelectorValue = (nameSummaryPartyDefailts?: Partydetails) => {

        if (!nameSummaryPartyDefailts) {
            return null;
        }

        if (!nameSummaryPartyDefailts.firstName && !nameSummaryPartyDefailts.lastName && !nameSummaryPartyDefailts.guid && !nameSummaryPartyDefailts.id) {
            return null;
        }

        const fullName = `${nameSummaryPartyDefailts.firstName} ${nameSummaryPartyDefailts.lastName}`;  // Concatenate firstName and lastName

        return {
            label: fullName,
            value: nameSummaryPartyDefailts.guid,
            nameId: nameSummaryPartyDefailts.id,
            guidID: nameSummaryPartyDefailts.guid,
            isFromDataSource: nameSummaryPartyDefailts.isFromDataSource
        };
    };

    const getPhoneNumberByType = (phoneNumbers: PhoneNumber[], type: 'Phone' | 'Mobile') => {
        if (phoneNumbers.length === 0) {
            return null;
        }
        return phoneNumbers.find(phone => phone.type === type) ?? null;
    };

    const getPartyFirstEmail = (emailAddresses: EmailAddress[]) => {
        if (emailAddresses.length === 0) {
            return null;
        }
        return emailAddresses[0];
    };

    if (props.partyDetails) {
        
        const isOrganisation = props.partyDetails.__typename === 'Organisation' ? true : false;

        initialValues = {
            ...initialValues,

            // individual
            firstName: !isOrganisation && props.partyDetails && props.partyDetails.firstName ? props.partyDetails.firstName : '',
            lastName: !isOrganisation && props.partyDetails && props.partyDetails.lastName ? props.partyDetails.lastName : '',
            // tslint:disable-next-line: no-any
            individualSelector: !isOrganisation ? getPartyDetailsIndividualSelectorValue(props.partyDetails) as any : null,
            nameGuid: !isOrganisation ? props.partyDetails && props.partyDetails.guid : null,
            // ...(props.partyDetails && props.partyDetails.entityTypeId === 0 && {   
            //     individualSelector: getEventsIndividualSelectorValue(props.partyDetails) as IndividualNameSummary,
            //     nameGuid: props.partyDetails && props.partyDetails.entityGuid ? props.partyDetails.entityGuid : null,
            // }),

            phone: props.partyDetails ? getPhoneNumberByType(props.partyDetails.phoneNumbers, 'Phone')?.number ?? '' : '',
            mobile: props.partyDetails ? getPhoneNumberByType(props.partyDetails.phoneNumbers, 'Mobile')?.number ?? '' : '',
            email: props.partyDetails.emailAddresses ? getPartyFirstEmail(props.partyDetails.emailAddresses)?.email ?? '' : '',

            organisation: isOrganisation ? props.partyDetails.longName ?? '' : '',
            organisationGuid: isOrganisation ? props.partyDetails.guid : null,
            organisationSelector: props.partyDetails.longName && props.partyDetails.guid
                ? {
                    label: props.partyDetails.longName,
                    value: props.partyDetails.guid
                } as unknown as OrganisationIAutoCompleteItem
                : null,
            isOrganisationSwitched: false,

            // organistaion
            // organisation: props.partyDetails ? props.partyDetails.organisation ?? '' : '',
            // organisationSelector:
            // props.partyDetails && props.partyDetails.organisation && props.partyDetails.organisationGuid
            //     ? { label: props.partyDetails.organisation, value: props.partyDetails.organisationGuid }
            //     : null,
            // organisationGuid: props.partyDetails && props.partyDetails.organisationGuid ? props.partyDetails.organisationGuid : null,
            // natureOfEnquiry: props.partyDetails && props.partyDetails.nameNotes ? removeTagsAndStyles(props.partyDetails.nameNotes)  : '',
        };
    }

    // set default value for address if exists in the party addresss
    if (partyAddressesQuery.data && partyAddressesQuery.data.party.addresses.address.length > 0 && !partyAddressesQuery.loading) {

        const addresses = getFirstAddressOfEachType(partyAddressesQuery.data);

        addresses.forEach((address) => {
            // tslint:disable-next-line: no-shadow-variable
            const { addressType, country, floor, name: buildingName, number: buildingNumber, postcode, state: addressState, street, suburb } = address;

            initialValues = {
                ...initialValues,
                ...(addressType === 'Street' && {
                    addressName: buildingName,
                    floor: floor,
                    number: buildingNumber,
                    street: street,
                    street2: '',
                    suburb: suburb,
                    state: addressState,
                    postcode: postcode,
                    country: country,
                }),
                ...(addressType === 'POBox' && {
                    pOBox: buildingNumber,
                    postalSuburb: suburb,
                    postalState: addressState,
                    postalPostCode: postcode,
                    postalCountry: country,
                })
            };
        });
    }

    return initialValues;
};
